import React from "react";
import { motion } from "framer-motion";
import { Plug, ArrowRight, CheckCircle2, FileSpreadsheet } from "lucide-react";

// Provider-agnostic accounting band. Adding QuickBooks/Sage later = flip `live` to true
// and drop in the matching screenshot/card — the copy and 3-step story already fit.
const PROVIDERS = [
  { name: "Xero", color: "#13B5EA", live: true },
  { name: "QuickBooks", color: "#2CA01C", live: false },
  { name: "Sage", color: "#00D639", live: false },
];

const STEPS = [
  { n: "1", t: "Build your valuation", d: "Progress billing with CIS & reverse-charge VAT." },
  { n: "2", t: "Client approves", d: "They approve the Application for Payment online." },
  { n: "3", t: "Draft invoice in your accounts", d: "Synced automatically, ready to approve." },
];

const AccountingSync = () => (
  <section id="accounting" className="py-24 bg-[#0e1f3a] text-white overflow-hidden" data-testid="accounting-sync-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
      {/* Left: copy + providers + steps */}
      <div>
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">
          <Plug className="h-4 w-4" /> Accounting sync
        </span>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Syncs with your accounting software
        </h2>
        <p className="mt-4 text-slate-300 max-w-lg">
          Turn any valuation into a draft invoice in your accounts in one click — client contact synced,
          CIS and reverse-charge VAT handled, with live Draft → Approved → Paid status back in TradeOff.
        </p>

        {/* Provider pills */}
        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="accounting-providers">
          {PROVIDERS.map((p) => (
            <span
              key={p.name}
              data-testid={`accounting-provider-${p.name.toLowerCase()}`}
              className={`inline-flex items-center gap-2 rounded-full pl-2.5 pr-3 py-1.5 text-sm font-semibold border ${
                p.live ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-slate-400"
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.live ? p.color : "#64748b" }} />
              {p.name}
              {p.live ? (
                <span className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300">Live</span>
              ) : (
                <span className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Soon</span>
              )}
            </span>
          ))}
        </div>

        {/* 3 steps */}
        <div className="mt-8 space-y-4">
          {STEPS.map((s) => (
            <div key={s.n} className="flex gap-3.5 items-start" data-testid={`accounting-step-${s.n}`}>
              <span className="shrink-0 h-7 w-7 rounded-full bg-sky-500/20 text-sky-300 font-bold text-sm flex items-center justify-center">{s.n}</span>
              <div>
                <h4 className="font-display font-semibold text-white">{s.t}</h4>
                <p className="text-sm text-slate-400">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-7 flex items-center gap-2 text-sm text-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-slate-300" />
          Not on Xero yet? Export a Xero, QuickBooks or Sage-ready invoice CSV anytime.
        </p>
      </div>

      {/* Right: the flow visual (valuation -> Xero draft invoice) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative"
        data-testid="accounting-visual"
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* TradeOff valuation card */}
          <div className="w-full sm:flex-1 rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">TradeOff · Valuation</span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-sky-300 bg-sky-500/15 rounded px-1.5 py-0.5">App. 3</span>
            </div>
            <p className="mt-2 text-sm font-medium text-white truncate">Riverside Court — Block A</p>
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-300"><span>Completed to date</span><span className="font-mono">£42,200.00</span></div>
              <div className="flex justify-between text-amber-300/90"><span>Retention 5%</span><span className="font-mono">− £2,110.00</span></div>
              <div className="flex justify-between text-rose-300/90"><span>CIS 20% (labour)</span><span className="font-mono">− £6,593.00</span></div>
              <div className="flex justify-between text-slate-400"><span>VAT — reverse charge</span><span className="font-mono">£0.00</span></div>
              <div className="flex justify-between border-t border-white/10 pt-1.5 font-semibold text-white"><span>Amount due</span><span className="font-mono text-sky-300">£33,497.00</span></div>
            </div>
          </div>

          {/* Arrow (points down on mobile, right on desktop) */}
          <div className="shrink-0 mx-auto sm:mx-0 h-9 w-9 rounded-full bg-sky-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
            <ArrowRight className="h-5 w-5 text-white rotate-90 sm:rotate-0" />
          </div>

          {/* Xero draft invoice card */}
          <div className="w-full sm:flex-1 rounded-xl bg-white text-slate-800 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5" style={{ backgroundColor: "#13B5EA" }}>
              <span className="font-display font-bold text-white text-sm tracking-tight">xero</span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-white/90">Sales invoice</span>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">INV-0042</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <CheckCircle2 className="h-3 w-3" /> Paid
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">Meridian Construction Ltd</p>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600"><span>Work completed to date</span><span className="font-mono">£42,200.00</span></div>
                <div className="flex justify-between text-slate-500"><span>Retention held</span><span className="font-mono">− £2,110.00</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-1.5 font-semibold text-slate-900"><span>Total</span><span className="font-mono">£40,090.00</span></div>
              </div>
              <p className="mt-2 text-[10px] text-slate-400 italic">CIS &amp; reverse charge calculated by Xero.</p>
            </div>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">Illustrative — CIS and VAT are calculated in your connected organisation.</p>
      </motion.div>
    </div>
  </section>
);

export default AccountingSync;
